import React, { useContext, useEffect, useState } from "react";
import { BellIcon } from "@heroicons/react/outline";
import AuthContext from "../context/AuthProvider";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import {
  getPushState,
  isIOS,
  requestPushPermission,
  syncPushSubscription,
} from "../utils/pushNotifications";

const DISMISS_KEY = "push_prompt_dismissed_until";
const DISMISS_DAYS = 30;

const isDismissed = () => {
  try {
    return Number(localStorage.getItem(DISMISS_KEY) || 0) > Date.now();
  } catch {
    return false;
  }
};

// Asks for notification permission from a tap, which iOS requires, and
// explains the Home Screen / Settings steps where the browser can't prompt.
const PushPrompt = () => {
  const { auth } = useContext(AuthContext);
  const axiosPrivate = useAxiosPrivate();
  const [state, setState] = useState(getPushState);
  const [dismissed, setDismissed] = useState(isDismissed);
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState("");

  // Permission can change in Settings while the app is in the background.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") setState(getPushState());
    };
    document.addEventListener("visibilitychange", refresh);
    return () => document.removeEventListener("visibilitychange", refresh);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setTimeout(() => setEnabled(false), 6000);
    return () => clearTimeout(timer);
  }, [enabled]);

  const dismiss = () => {
    try {
      localStorage.setItem(
        DISMISS_KEY,
        String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000)
      );
    } catch {}
    setDismissed(true);
  };

  const enable = async () => {
    setBusy(true);
    setError("");
    try {
      // First await in the tap handler, so iOS shows the prompt.
      const permission = await requestPushPermission();
      setState(permission);
      if (permission === "granted") {
        await syncPushSubscription(axiosPrivate, auth?.user);
        setEnabled(true);
      }
    } catch (err) {
      console.error("[push] Enabling notifications failed", err);
      setError("Η ενεργοποίηση απέτυχε. Δοκιμάστε ξανά.");
    } finally {
      setBusy(false);
    }
  };

  if (enabled) {
    return (
      <div className="push-prompt push-prompt--done" role="status">
        <BellIcon className="push-prompt-icon" aria-hidden="true" />
        <p className="push-prompt-text">
          Οι ειδοποιήσεις ενεργοποιήθηκαν. Θα λάβετε ένα μήνυμα καλωσορίσματος.
        </p>
      </div>
    );
  }

  const device = /iPhone|iPod/.test(navigator.userAgent) ? "iPhone" : "iPad";
  let text;
  let action = null;
  if (state === "default") {
    text =
      "Ενεργοποιήστε τις ειδοποιήσεις για να ενημερώνεστε άμεσα για τις υποθέσεις, τις προθεσμίες και τα σχόλιά σας.";
    action = (
      <button
        type="button"
        className="push-prompt-button"
        onClick={enable}
        disabled={busy}
      >
        {busy ? "Ενεργοποίηση…" : "Ενεργοποίηση"}
      </button>
    );
  } else if (state === "needs-install") {
    text = `Για ειδοποιήσεις στο ${device}: πατήστε Κοινή χρήση και «Προσθήκη στην οθόνη Αφετηρίας», και ανοίξτε το MySolon από την οθόνη Αφετηρίας.`;
  } else if (state === "denied" && isIOS()) {
    text =
      "Οι ειδοποιήσεις είναι απενεργοποιημένες. Ενεργοποιήστε τες από Ρυθμίσεις → Ειδοποιήσεις → MySolon.";
  }

  if (!auth?.user || dismissed || !text) return null;

  return (
    <div className="push-prompt">
      <BellIcon className="push-prompt-icon" aria-hidden="true" />
      <div className="push-prompt-body">
        <p className="push-prompt-text">{text}</p>
        {error && <p className="push-prompt-error">{error}</p>}
        {action}
      </div>
      <button
        type="button"
        className="push-prompt-close"
        onClick={dismiss}
        aria-label="Κλείσιμο"
        title="Απόκρυψη για 30 ημέρες"
      >
        ×
      </button>
    </div>
  );
};

export default PushPrompt;
